import type { FxFile } from './client.js';

/**
 * A path inside one segment, below `<partner>/<segment>/<mode>/`: `[]` is the segment root,
 * `['enrollment', '2026', '11']` a date level inside the `enrollment` partner folder.
 */
export type FolderPath = readonly string[];

export interface FolderListing {
  /** Child folder names: partner folders A–Z, then date levels newest first. */
  readonly folders: readonly string[];
  /** Files directly at this level, in the order listed (the API lists newest first). */
  readonly files: readonly FxFile[];
}

/** `<partner>/<segment>/<mode>` — stripped so the delivery mode never shows up as a folder. */
const AREA_DEPTH = 3;
/**
 * The partner-record folder slug. It must contain a letter, so a date segment never matches.
 * Same rule as `folderSlugSchema` in @nrx/betty-contracts and `FOLDER_RE` in nrx-file-exchange's
 * `prefix.ts`; change all three together.
 */
const PARTNER_FOLDER = /^(?=.*[a-z])[a-z0-9]+(-[a-z0-9]+)*$/;
const STATUS_MARKERS: ReadonlySet<string> = new Set(['_failed', '_quarantine']);
const DIGITS = /^\d+$/;

interface Entry {
  readonly file: FxFile;
  /** Directories below the area, status marker removed. */
  readonly dirs: readonly string[];
}

function toEntry(file: FxFile): Entry {
  const parts = file.key.split('/').filter((p) => p !== '');
  const dirs = parts.slice(AREA_DEPTH, -1);
  // A status marker sits immediately before the filename and files beside its siblings.
  const last = dirs[dirs.length - 1];
  return { file, dirs: last !== undefined && STATUS_MARKERS.has(last) ? dirs.slice(0, -1) : dirs };
}

function startsWith(dirs: readonly string[], path: FolderPath): boolean {
  return path.every((p, i) => dirs[i] === p);
}

function compareFolders(a: string, b: string): number {
  const aDate = DIGITS.test(a);
  const bDate = DIGITS.test(b);
  if (aDate !== bDate) return aDate ? 1 : -1;
  return aDate ? b.localeCompare(a) : a.localeCompare(b);
}

/**
 * Groups a segment's recursive listing into what one level of the browser shows.
 *
 * At the segment root only partner folders open; every file outside one (a date-partitioned
 * inbound file, a flat or dated outbound file) is a loose file listed flat, exactly as before
 * folders existed. Inside a partner folder every directory level opens, dates included.
 *
 * `allowed` is the partner's folder allowlist from the list response. The listing carries no
 * directory markers, so a folder nothing has been written to yet appears only through it.
 */
export function listFolder(
  files: readonly FxFile[],
  path: FolderPath,
  allowed: readonly string[] = [],
): FolderListing {
  const folders = new Set<string>(path.length === 0 ? allowed : []);
  const here: FxFile[] = [];
  for (const { dirs, file } of files.map(toEntry)) {
    if (path.length === 0) {
      const top = dirs[0];
      if (top !== undefined && PARTNER_FOLDER.test(top)) folders.add(top);
      else here.push(file);
      continue;
    }
    if (!startsWith(dirs, path)) continue;
    const child = dirs[path.length];
    if (child !== undefined) folders.add(child);
    else here.push(file);
  }
  return { folders: [...folders].sort(compareFolders), files: here };
}

/** The `folder` an upload at this path sends: the partner folder, since dates are assigned server-side. */
export function uploadFolder(path: FolderPath): string | undefined {
  return path[0];
}

/**
 * Where an uploaded key lists in the browser: its directories inside a partner folder, or `[]` for
 * a file outside one, which lists at the segment root. The server picks the date levels, so this
 * can differ from the path the upload was made from.
 */
export function landingPath(key: string): FolderPath {
  const { dirs } = toEntry({ key, name: '', size: 0, lastModified: '' });
  const top = dirs[0];
  return top !== undefined && PARTNER_FOLDER.test(top) ? dirs : [];
}
